/**
 * Calculate the difference between original BOM state and current state
 * Returns lists of creates, updates, and deletes needed
 * Also groups changes by parent item for multi-level awareness
 */
export const calculateBOMDiff = (originalState, currentState) => {

  console.log('=== DIFF DEBUG ===');
  console.log('Original nodes:', originalState.nodes.length);
  console.log('Current nodes:', currentState.nodes.length);
  console.log('Original edges:', originalState.edges.length);
  console.log('Current edges:', currentState.edges.length);
  const diff = {
    created: [],
    updated: [],
    deleted: [],
    byParent: {},
  };

  const { nodes: originalNodes, edges: originalEdges } = originalState;
  const { nodes: currentNodes, edges: currentEdges } = currentState;

  // Create node lookup maps by node ID -> itemId
  const originalNodeToItem = new Map();
  originalNodes.forEach(node => {
    originalNodeToItem.set(node.id, node.data?.itemId);
  });

  const currentNodeToItem = new Map();
  currentNodes.forEach(node => {
    currentNodeToItem.set(node.id, node.data?.itemId);
  });

  // Create node lookup for getting item details (by node ID)
  const nodeMap = new Map();
  currentNodes.forEach(node => {
    nodeMap.set(node.id, node);
  });
  originalNodes.forEach(node => {
    if (!nodeMap.has(node.id)) {
      nodeMap.set(node.id, node);
    }
  });

  // Helper to get edge key based on item IDs (not node IDs)
  const getEdgeItemKey = (edge, nodeToItemMap) => {
    const sourceItemId = nodeToItemMap.get(edge.source);
    const targetItemId = nodeToItemMap.get(edge.target);
    if (!sourceItemId || !targetItemId) return null;
    return `${sourceItemId}->${targetItemId}`;
  };

  // Create edge maps using item IDs as keys
  const originalEdgeMap = new Map();
  originalEdges.forEach(edge => {
    const key = getEdgeItemKey(edge, originalNodeToItem);
    if (key) {
      originalEdgeMap.set(key, edge);
    }
  });

  const currentEdgeMap = new Map();
  currentEdges.forEach(edge => {
    const key = getEdgeItemKey(edge, currentNodeToItem);
    if (key) {
      currentEdgeMap.set(key, edge);
    }
  });

  // Helper to add change to byParent grouping
  const addToParentGroup = (parentId, parentCode, changeType, changeData) => {
    if (!parentId) return;
    if (!diff.byParent[parentId]) {
      diff.byParent[parentId] = {
        parentId,
        parentCode,
        created: [],
        updated: [],
        deleted: [],
      };
    }
    diff.byParent[parentId][changeType].push(changeData);
  };

  // Find created and updated edges
  currentEdges.forEach(edge => {
    const key = getEdgeItemKey(edge, currentNodeToItem);
    if (!key) return;

    const originalEdge = originalEdgeMap.get(key);
    const sourceNode = nodeMap.get(edge.source);
    const targetNode = nodeMap.get(edge.target);

    if (!sourceNode || !targetNode) return;

    const parentId = targetNode.data?.itemId;
    const parentCode = targetNode.data?.itemCode;
    const childId = sourceNode.data?.itemId;
    const childCode = sourceNode.data?.itemCode;

    if (!originalEdge) {
      // New edge - needs to be created
      const createData = {
        parentId,
        childId,
        parentCode,
        childCode,
        quantity: edge.data?.quantity || 1,
        sequence: edge.data?.sequence || 1,
      };
      diff.created.push(createData);
      addToParentGroup(parentId, parentCode, 'created', createData);
    } else {
      // Existing edge - check if quantity changed
      const originalQty = parseFloat(originalEdge.data?.quantity) || 1;
      const currentQty = parseFloat(edge.data?.quantity) || 1;

      if (Math.abs(originalQty - currentQty) > 0.000001) {
        const updateData = {
          bomId: originalEdge.data?.bomId,
          parentId,
          childId,
          parentCode,
          childCode,
          oldQuantity: originalQty,
          quantity: currentQty,
        };
        diff.updated.push(updateData);
        addToParentGroup(parentId, parentCode, 'updated', updateData);
      }
    }
  });

  // Find deleted edges
  originalEdges.forEach(edge => {
    const key = getEdgeItemKey(edge, originalNodeToItem);
    if (!key) return;

    if (!currentEdgeMap.has(key)) {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);

      // Try to find nodes by itemId if not found by node ID
      let parentId = targetNode?.data?.itemId;
      let parentCode = targetNode?.data?.itemCode;
      let childId = sourceNode?.data?.itemId;
      let childCode = sourceNode?.data?.itemCode;

      // Extract from key if nodes not found
      if (!parentId || !childId) {
        const [srcItemId, tgtItemId] = key.split('->');
        childId = childId || srcItemId;
        parentId = parentId || tgtItemId;
        
        // Try to find codes from current nodes
        currentNodes.forEach(n => {
          if (n.data?.itemId === childId && !childCode) childCode = n.data?.itemCode;
          if (n.data?.itemId === parentId && !parentCode) parentCode = n.data?.itemCode;
        });
        originalNodes.forEach(n => {
          if (n.data?.itemId === childId && !childCode) childCode = n.data?.itemCode;
          if (n.data?.itemId === parentId && !parentCode) parentCode = n.data?.itemCode;
        });
      }

      if (!parentId || !childId) return;

      const deleteData = {
        bomId: edge.data?.bomId,
        parentId,
        childId,
        parentCode: parentCode || 'Unknown',
        childCode: childCode || 'Unknown',
        quantity: edge.data?.quantity || 1,
      };
      diff.deleted.push(deleteData);
      addToParentGroup(parentId, parentCode, 'deleted', deleteData);
    }
  });

  console.log('Created:', diff.created.length, diff.created);
  console.log('Updated:', diff.updated.length, diff.updated);
  console.log('Deleted:', diff.deleted.length, diff.deleted);

  return diff;
};

/**
 * Get list of parent items that have been modified
 * Returns array of { parentId, parentCode, changeCount, isTargetItem }
 */
export const getModifiedParents = (diff, targetItemId) => {
  if (!diff || !diff.byParent) {
    return [];
  }

  const parents = [];
  
  Object.values(diff.byParent).forEach(parent => {
    const changeCount = parent.created.length + parent.updated.length + parent.deleted.length;
    if (changeCount > 0) {
      parents.push({
        parentId: parent.parentId,
        parentCode: parent.parentCode,
        changeCount,
        isTargetItem: parent.parentId === targetItemId,
        created: parent.created.length,
        updated: parent.updated.length,
        deleted: parent.deleted.length,
      });
    }
  });

  // Sort: target first, then by code
  parents.sort((a, b) => {
    if (a.isTargetItem && !b.isTargetItem) return -1;
    if (!a.isTargetItem && b.isTargetItem) return 1;
    return (a.parentCode || '').localeCompare(b.parentCode || '');
  });

  return parents;
};

/**
 * Generate a human-readable summary of changes
 */
export const generateDiffSummary = (diff) => {
  const lines = [];

  if (diff.created.length > 0) {
    lines.push(`CREATE ${diff.created.length} new BOM relationship(s):`);
    diff.created.forEach(item => {
      lines.push(`  + ${item.childCode} -> ${item.parentCode} (qty: ${item.quantity})`);
    });
  }

  if (diff.updated.length > 0) {
    lines.push(`UPDATE ${diff.updated.length} quantity(ies):`);
    diff.updated.forEach(item => {
      lines.push(`  ~ ${item.childCode} -> ${item.parentCode}: ${item.oldQuantity} -> ${item.quantity}`);
    });
  }

  if (diff.deleted.length > 0) {
    lines.push(`DELETE ${diff.deleted.length} BOM relationship(s):`);
    diff.deleted.forEach(item => {
      lines.push(`  - ${item.childCode} -> ${item.parentCode}`);
    });
  }

  if (lines.length === 0) {
    lines.push('No changes detected');
  }

  return lines.join('\n');
};

/**
 * Check if there are any unsaved changes
 */
export const hasChanges = (originalState, currentState) => {
  const diff = calculateBOMDiff(originalState, currentState);
  return diff.created.length > 0 || diff.updated.length > 0 || diff.deleted.length > 0;
};

/**
 * Deep clone state for comparison
 */
export const cloneState = (state) => {
  return {
    nodes: state.nodes.map(node => ({
      ...node,
      data: { ...node.data },
      position: { ...node.position },
    })),
    edges: state.edges.map(edge => ({
      ...edge,
      data: { ...edge.data },
    })),
  };
};